import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Typography,
  Alert,
  Box,
  Chip,
  Stack,
  CircularProgress
} from '@mui/material';
import api from '../../config/api';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

// API helpers colocated (frontend/src/services is outside this bot's write scope,
// same as ProductDetailTabs.js).
const getAssignableAssemblies = (brandId, sku) =>
  api.get(`${API_BASE}/api/cogs/brands/${brandId}/components/${encodeURIComponent(sku)}/assignable-assemblies`)
    .then((r) => r.data);
export const addComponentToBoms = (brandId, sku, payload) =>
  api.post(`${API_BASE}/api/cogs/brands/${brandId}/components/${encodeURIComponent(sku)}/add-to-boms`, payload)
    .then((r) => r.data);

const todayStr = () => new Date().toISOString().slice(0, 10);

/**
 * Post-create quick-assign: "component created — add it to finished-goods
 * recipes now?" Lists every sellable product: ones with an active production
 * recipe get the component added (ones already containing it are disabled);
 * ones without (has_recipe=false) get their FIRST recipe created with it
 * (Rishabh 2026-07-03 "I want both"). Takes a quantity per selected FG + a
 * start date, writes everything in one call. Skippable — closing changes
 * nothing.
 */
function AddToRecipesDialog({ open, brandId, componentSku, componentName, onClose, onAssigned }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [effectiveFrom, setEffectiveFrom] = useState(todayStr());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !brandId || !componentSku) return;
    setOptions([]);
    setSelected([]);
    setQuantities({});
    setEffectiveFrom(todayStr());
    setError(null);
    setLoading(true);
    getAssignableAssemblies(brandId, componentSku)
      .then((data) => setOptions(data.assemblies || []))
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load finished goods')))
      .finally(() => setLoading(false));
  }, [open, brandId, componentSku]);

  const optionLabel = (o) => `${o.assembly_sku}${o.product_name ? ` — ${o.product_name}` : ''}`;

  const quantityOf = (sku) => {
    const q = quantities[sku];
    return q === undefined ? '1' : q;
  };
  const allQuantitiesValid = selected.every((o) => {
    const q = Number(quantityOf(o.assembly_sku));
    return Number.isFinite(q) && q > 0;
  });

  const handleAssign = async () => {
    try {
      setSaving(true);
      setError(null);
      const result = await addComponentToBoms(brandId, componentSku, {
        assignments: selected.map((o) => ({
          assembly_sku: o.assembly_sku,
          quantity: Number(quantityOf(o.assembly_sku))
        })),
        effective_from: effectiveFrom
      });
      onAssigned(result);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to add to recipes'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {componentSku} created — add it to recipes now?
        {componentName ? (
          <Typography variant="body2" color="text.secondary">{componentName}</Typography>
        ) : null}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick the finished goods whose recipe should include this component — one click adds it
          to all of them. Skip if you&apos;d rather do it later from each product&apos;s BOM tab.
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Autocomplete
              multiple
              options={options}
              value={selected}
              onChange={(e, val) => setSelected(val)}
              getOptionLabel={optionLabel}
              isOptionEqualToValue={(o, v) => o.assembly_sku === v.assembly_sku}
              getOptionDisabled={(o) => !!o.already_has}
              renderOption={(props, o) => (
                <li {...props} key={o.assembly_sku}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2">{o.assembly_sku}</Typography>
                      {o.product_name ? (
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {o.product_name}
                        </Typography>
                      ) : null}
                    </Box>
                    {o.already_has ? <Chip size="small" label="already in recipe" /> : null}
                    {!o.already_has && o.has_recipe === false
                      ? <Chip size="small" color="warning" variant="outlined" label="no recipe yet — will create" />
                      : null}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Finished goods"
                  placeholder="Search by SKU or name"
                  helperText='Products marked "no recipe yet" get their first recipe created with just this component — keep assigning their other parts as you create them.'
                />
              )}
            />
            {selected.length > 0 && (
              <Stack spacing={1} sx={{ mt: 2 }}>
                {selected.map((o) => (
                  <Box key={o.assembly_sku} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {o.assembly_sku}
                        {o.has_recipe === false ? (
                          <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                            new recipe
                          </Typography>
                        ) : null}
                      </Typography>
                      {o.product_name ? (
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {o.product_name}
                        </Typography>
                      ) : null}
                    </Box>
                    <TextField
                      label="Qty per unit"
                      type="number"
                      size="small"
                      sx={{ width: 130 }}
                      value={quantityOf(o.assembly_sku)}
                      onChange={(e) =>
                        setQuantities((q) => ({ ...q, [o.assembly_sku]: e.target.value }))}
                      inputProps={{ min: 0, step: 'any' }}
                      error={!(Number(quantityOf(o.assembly_sku)) > 0)}
                    />
                  </Box>
                ))}
                <TextField
                  label="In recipes from"
                  type="date"
                  size="small"
                  sx={{ width: 200, mt: 1 }}
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Builds before this date are unaffected"
                />
              </Stack>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Skip</Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={saving || loading || selected.length === 0 || !allQuantitiesValid || !effectiveFrom}
        >
          {saving
            ? 'Adding…'
            : `Add to ${selected.length} recipe${selected.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddToRecipesDialog;
